import { Attachment, Document, Filter, Reference, SkPageInput } from 'sr-types/lib/search/v1/graphql';
import { ContactInput, WorkflowInfo, WorkflowInfoInput } from 'sr-types/lib/workflow/v1/graphql';
import { FILTER_IDENTIFIERS } from '../../constants/constants';
import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import { getContactInput } from '../../contacts/getContactInput';
import associateTag from '../../utils/associateTag';
import {
    APPROVAL_CONTACT_ROLE,
    APPROVAL_PROVIDER,
    APPROVAL_STATUS,
    APPROVAL_STEP_ID_NAMESPACE,
    APPROVAL_STEP_STATUS,
    APPROVAL_STRATEGIES,
    WorkflowEntityReferenceMissingError,
    WorkflowIsSequentialValueInvalidError,
    WorkflowStatusValueInvalidError,
    WorkflowStepAlternateIdLabelInvalidError,
    WorkflowStepAlternateIdMissingError,
    WorkflowStepContactMissingError,
    WorkflowStepOccurredAtValueInvalidError,
    WorkflowStepStatusValueInvalidError
} from '../constants';
import {
    ApprovalDataViewModel,
    ApprovalInputViewModel,
    ApprovalStatus,
    ApprovalStep,
    ApprovalStepStatus,
    ApprovalStrategy,
    DocumentSearchQueryData,
    WorkflowInfoQueryData
} from '../types';

/**
 * Associates tags with the given attachments.
 *
 * @param attachments The list of attachments to associate tags for
 * @param activeOrganizationAccount The active organization account for the logged-in user
 */
export async function associateTagsWithAttachments(attachments: Attachment[], activeOrganizationAccount: string) {
    return Promise.all(
        attachments
            .map((attachment) => {
                if (attachment.tags?.length > 0) {
                    return associateTag(activeOrganizationAccount, attachment.entityReference.id, attachment.tags);
                }
            })
            .filter(Boolean)
    );
}

/**
 * Transforms an approval data input view model to a `saveWorkflow` API input.
 *
 * @param approvalDataInput The new approval request data to be converted to a workflow input
 * @returns The converted workflow input
 */
export function transformApprovalInputViewModelToWorkflowInput(
    approvalDataInput: ApprovalInputViewModel
): WorkflowInfoInput {
    const { entityReference, summary, attachments, approverContacts, notifyContacts } = approvalDataInput;

    const documentReferences = attachments?.map((attachment) => attachment.entityReference) || [];

    const isSequential = approvalDataInput.approvalStrategy === APPROVAL_STRATEGIES.allApprovalsRequiredInSequence;

    const contactInputs: ContactInput[] = approverContacts.map(getContactInput);
    const notifyTo: ContactInput[] = (notifyContacts ?? []).map(getContactInput);

    const workflowInfoInput: WorkflowInfoInput = {
        status: APPROVAL_STATUS.draft,
        context: {
            entityReference,
            documentReferences
        },
        coverNote: summary,
        workflowSteps: contactInputs.map((contactInput) => ({
            provider: APPROVAL_PROVIDER.internal,
            role: APPROVAL_CONTACT_ROLE.approver,
            isSequential: isSequential,
            workflowParticipant: {
                contacts: [contactInput]
            }
        })),
        notifyTo
    };

    return workflowInfoInput;
}

/**
 * Transforms documents search query data to approval attachments data.
 *
 * @param documentsData Result of the documents search query
 * @returns A list of attachments mapped from the documents data
 */
export function transformDocumentsDataToApprovalAttachmentsData(
    documentsData: DocumentSearchQueryData | undefined
): ApprovalDataViewModel['attachments'] {
    if (!documentsData) {
        return [];
    }

    return (
        documentsData?.search?.hits?.items?.map((document: Document) => {
            const { id, uri, headline, size, contentType, isLink, tags, security, createdOn, reference } = document;

            const mappedAttachment: Attachment = {
                id,
                uri,
                headline,
                name: headline,
                size,
                type: contentType,
                isLink,
                tags,
                createdOn,
                security,
                entityReference: reference
            };

            return mappedAttachment;
        }) || []
    );
}

/**
 * Transforms workflow query data and approval attachments data to approval data.
 * Also validates the data for correctness, and throws errors if invalid data is found.
 *
 * @param workflowData Result of the fetch workflow query
 * @param approvalAttachmentsData A list of approval attachments
 * @returns The transformed approval data
 */
export function transformWorkflowDataToApprovalData(
    workflowData: WorkflowInfoQueryData,
    approvalAttachmentsData: Attachment[]
): ApprovalDataViewModel | undefined {
    const workflow = workflowData?.workflow;

    if (!workflow) {
        return undefined;
    }

    const status = workflow.status as ApprovalStatus;
    if (!Object.values(APPROVAL_STATUS).includes(status)) {
        throw new WorkflowStatusValueInvalidError(status);
    }

    const summary = workflow.coverNote;

    const entityReference = workflow.context?.entityReference;
    if (!entityReference) {
        throw new WorkflowEntityReferenceMissingError();
    }

    const attachments = approvalAttachmentsData;

    const steps: ApprovalStep[] = transformWorkflowStepsDataToApprovalStepsData(workflow);

    const approvalStrategy = inferApprovalStrategyFromWorkflowData(workflow);

    const notifyContacts: ApprovalDataViewModel['notifyContacts'] = workflow.notifyTo ?? [];

    const approvalData: ApprovalDataViewModel = {
        status,
        summary,
        entityReference,
        attachments,
        steps,
        approvalStrategy,
        notifyContacts
    };

    return approvalData;
}

/**
 * Transforms workflow steps data to approval step data.
 * Also validates the data for correctness, and throws errors if invalid data is found.
 *
 * @param workflow Result of the fetch workflow query
 * @returns The transformed approval step data
 */
export function transformWorkflowStepsDataToApprovalStepsData(workflow: WorkflowInfo): ApprovalStep[] {
    let approvalStepsData: ApprovalStep[] = [];

    const workflowSteps = workflow.workflowSteps?.filter(
        (workflowStep) => workflowStep.role === APPROVAL_CONTACT_ROLE.approver
    );

    if (!workflowSteps || workflowSteps.length === 0) {
        return [];
    }

    approvalStepsData = workflowSteps.map((workflowStep, workflowStepIndex) => {
        const id = workflowStep.alternateId?.value;
        if (!id) {
            throw new WorkflowStepAlternateIdMissingError(workflowStepIndex);
        }

        const idNamespace = workflowStep.alternateId?.label as ApprovalStep['idNamespace'];
        if (idNamespace !== APPROVAL_STEP_ID_NAMESPACE) {
            throw new WorkflowStepAlternateIdLabelInvalidError(workflowStepIndex);
        }

        const canAct = workflowStep.canAct ?? false;

        const contact = workflowStep.workflowParticipant?.contacts?.[0];
        if (!contact) {
            throw new WorkflowStepContactMissingError(workflowStepIndex);
        }

        const status: ApprovalStepStatus =
            (workflowStep.workflowStepResult?.result as ApprovalStepStatus) || APPROVAL_STEP_STATUS.pending;
        if (!Object.values(APPROVAL_STEP_STATUS).includes(status)) {
            throw new WorkflowStepStatusValueInvalidError(workflowStepIndex, status);
        }

        const comment = workflowStep.workflowStepResult?.comment || '';

        let occurredAt: Date | null = null;
        const maybeOccurredAtResponseData = workflowStep.workflowStepResult?.occurredAt;
        if (maybeOccurredAtResponseData) {
            occurredAt = new Date(maybeOccurredAtResponseData);
            if (Number.isNaN(occurredAt.getTime())) {
                throw new WorkflowStepOccurredAtValueInvalidError(workflowStepIndex, maybeOccurredAtResponseData);
            }
        }

        const approvalStepData: ApprovalStep = {
            id,
            idNamespace,
            contact,
            canAct,
            status,
            comment,
            occurredAt
        };

        return approvalStepData;
    });

    return approvalStepsData;
}

/**
 * Infers the approval strategy from the workflow steps data.
 * Also validate the data for consistency, and throws errors if invalid data is found.
 *
 * @param workflow Result of the fetch workflow query
 * @returns The inferred approval strategy
 */
export function inferApprovalStrategyFromWorkflowData(workflow: WorkflowInfo): ApprovalStrategy {
    const workflowSteps = workflow.workflowSteps?.filter(Boolean);

    if (!workflowSteps || workflowSteps.length === 0) {
        return APPROVAL_STRATEGIES.allApprovalsRequired;
    }

    const firstIsSequential = workflowSteps[0].isSequential;
    for (const step of workflowSteps) {
        if (step.isSequential === undefined || step.isSequential !== firstIsSequential) {
            throw new WorkflowIsSequentialValueInvalidError();
        }
    }

    if (firstIsSequential === false) {
        return APPROVAL_STRATEGIES.allApprovalsRequired;
    }

    return APPROVAL_STRATEGIES.allApprovalsRequiredInSequence;
}

/**
 * Makes query variables for the documents search query.
 *
 * @param documentReferences A list of document references to be searched
 * @returns The constructed query variables
 */
export function makeDocumentsSearchQueryVariables(documentReferences: Reference[]): {
    entity: string;
    filter: Filter;
    page: SkPageInput;
} {
    const variables = {
        entity: ENTITY_CONSTANTS.document,
        filter: {
            expressions: [
                {
                    field: FILTER_IDENTIFIERS.identity,
                    value: {
                        values: documentReferences.map((doc) => doc.id)
                    }
                }
            ]
        },
        page: { from: 0, size: 200 }
    };

    return variables;
}
